
import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import { useApp } from '../../context/AppContext';

interface HeaderProps {
  isAdminView: boolean;
  setIsAdminView: (value: boolean) => void;
  unreadNotifications: number;
  onSignOut?: () => void;
}

const Header: React.FC<HeaderProps> = ({ isAdminView, setIsAdminView, unreadNotifications, onSignOut }) => {
  const { activeTab, setActiveTab, selectedProduct, setSelectedProduct, adminUser, currentUser, notifications, markNotificationRead, viewMode, setViewMode } = useApp();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Subscribers usually don't set a profile avatar; render an initial
  // chip instead of a broken-image icon if there's no URL or it 404s.
  const avatarUrl = currentUser.avatar;
  const initial = (currentUser.displayName || currentUser.username || '?').trim().charAt(0).toUpperCase() || '?';
  const showAvatarImg = !!avatarUrl && !avatarBroken;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTitle = () => {
    if (selectedProduct) return 'Product Details';
    switch (activeTab) {
      case 'home': return adminUser.displayName;
      case 'shop': return 'Shop';
      case 'cart': return 'My Cart';
      case 'messages': return 'Messages';
      case 'profile': return isAdminView ? 'Creator Dashboard' : 'My Profile';
      default: return 'OnlyJalen';
    }
  };

  const showBackButton = selectedProduct !== null;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'post': return '📸';
      case 'message': return '💬';
      case 'order': return '📦';
      case 'product': return '🛍️';
      case 'tip': return '💰';
      case 'welcome': return '🎉';
      default: return '🔔';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleNotificationClick = (notificationId: string) => {
    markNotificationRead(notificationId);
  };

  const handleMarkAllRead = async () => {
    // Single PUT /api/notifications/read-all instead of N PUTs. The
    // local state is updated optimistically via markNotificationRead so
    // the badge clears immediately; the API call reconciles server-side.
    notifications.forEach(n => { if (!n.read) markNotificationRead(n.id); });
    try {
      // Best-effort — local state is already correct so we can fail open.
      const { notificationsAPI } = await import('../../services/api');
      await notificationsAPI.markAllAsRead();
    } catch (e) {
      console.error('[markAllRead] backend call failed:', e);
    }
  };

  return (
    <header className={`header ${viewMode}`}>
      <div className="header-left">
        {showBackButton ? (
          <button className="header-btn back-btn" onClick={() => setSelectedProduct(null)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        ) : (
          <div className="header-logo">
            <div className="logo-icon">
              <img src="https://jalenward.com/images/blackwhitetextjalen.png" alt="OnlyJalen" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          </div>
        )}
      </div>

      <div className="header-center">
        <h1 className="header-title">{getTitle()}</h1>
        {activeTab === 'home' && !showBackButton && (
          <span className="header-subtitle">Exclusive Content & Fashion</span>
        )}
      </div>

      <div className="header-right">
        <button
          className="header-btn theme-toggle-btn"
          onClick={() => setViewMode(viewMode === 'dark' ? 'light' : 'dark')}
          title={viewMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle theme"
        >
          {viewMode === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>
        {/* Admin preview toggle is rendered above the app as AdminPreviewBar
            so it never bleeds into the fan-facing chrome. */}

        {/* User Menu with Sign Out */}
        <div className="user-menu-container" ref={userMenuRef}>
          <button className="header-btn user-menu-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
            {showAvatarImg
              ? <img
                  src={avatarUrl}
                  alt={currentUser.displayName}
                  className="user-menu-avatar"
                  onError={() => setAvatarBroken(true)}
                />
              : <span className="user-menu-avatar user-menu-avatar--initial" aria-hidden="true">{initial}</span>}
          </button>

          {showUserMenu && (
            <div className="user-menu-dropdown">
              <div className="user-menu-header">
                {showAvatarImg
                  ? <img src={avatarUrl} alt={currentUser.displayName} className="user-menu-profile-pic" onError={() => setAvatarBroken(true)} />
                  : <span className="user-menu-profile-pic user-menu-profile-pic--initial" aria-hidden="true">{initial}</span>}
                <div className="user-menu-info">
                  <span className="user-menu-name">{currentUser.displayName}</span>
                  <span className="user-menu-username">@{currentUser.username}</span>
                </div>
              </div>
              <div className="user-menu-divider" />
              <div className="user-menu-items">
                <button
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); setActiveTab('profile'); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  My Profile
                </button>
                {onSignOut && (
                  <button className="user-menu-item signout" onClick={() => { setShowUserMenu(false); onSignOut(); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                      <polyline points="16,17 21,12 16,7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Sign Out
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="notification-container" ref={notificationRef}>
          <button className="header-btn notification-btn" onClick={() => setShowNotifications(!showNotifications)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {unreadNotifications > 0 && (
                  <button className="mark-all-read" onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <span>🔔</span>
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? '' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification.id)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">{formatTimeAgo(new Date(notification.timestamp))}</div>
                      </div>
                      {!notification.read && <div className="notification-dot" />}
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="notification-footer">
                  <button onClick={() => setShowNotifications(false)}>View All</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
